import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const HISTORY_KEY = 'nexosynx_maya_conversations';

const SUGGESTED_QUESTIONS = [
  'Como vejo minhas estatísticas do CRM?',
  'Como adiciono um novo lead?',
  'Como funciona a análise de IA dos leads?',
  'Como crio uma tarefa?'
];

function loadHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveHistory(list) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(list.slice(0, 30)));
  } catch {
    // localStorage indisponível — ignora
  }
}

export default function ChatBot({ api, isMinimized, onToggle, proactiveMessage }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState(() => loadHistory());
  const [sessionId] = useState(() => 'chat_' + Date.now());
  const messagesEndRef = useRef(null);
  const lastProactiveRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!proactiveMessage || proactiveMessage === lastProactiveRef.current) return;
    lastProactiveRef.current = proactiveMessage;
    const coachMsg = {
      id: Date.now(),
      type: 'bot',
      content: proactiveMessage,
      timestamp: new Date(),
      isCoach: true,
      quickReplies: ['Ver detalhes', 'Quais negócios estão em risco?', 'Sugestões de ação']
    };
    setMessages(prev => [...prev, coachMsg]);
    setShowHistory(false);
    if (onToggle && typeof onToggle === 'function') {
      onToggle('open');
    }
  }, [proactiveMessage]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const persistCurrentConversation = (msgs) => {
    if (!msgs || msgs.length === 0) return;
    const firstUser = msgs.find(m => m.type === 'user');
    const title = (firstUser?.content || msgs[0]?.content || 'Conversa').slice(0, 60);
    const entry = { id: sessionId, title, messages: msgs, updatedAt: new Date().toISOString() };
    setConversations(prev => {
      const without = prev.filter(c => c.id !== sessionId);
      const next = [entry, ...without];
      saveHistory(next);
      return next;
    });
  };

  const handleNewConversation = () => {
    persistCurrentConversation(messages);
    setMessages([]);
    setInputMessage('');
    setShowHistory(false);
  };

  const handleOpenHistory = () => {
    persistCurrentConversation(messages);
    setShowHistory(true);
  };

  const handleSelectConversation = (conv) => {
    setMessages(conv.messages || []);
    setShowHistory(false);
  };

  const handleDeleteConversation = (e, id) => {
    e.stopPropagation();
    setConversations(prev => {
      const next = prev.filter(c => c.id !== id);
      saveHistory(next);
      return next;
    });
  };

  const handleSendMessage = async (message = inputMessage) => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    setTimeout(async () => {
      try {
        const response = await processMessage(message);

        const botMessage = {
          id: Date.now() + 1,
          type: 'bot',
          content: response.content,
          timestamp: new Date(),
          quickReplies: response.quickReplies,
          actions: response.actions
        };

        setMessages(prev => [...prev, botMessage]);
      } catch (error) {
        console.error('Chat error:', error);
        const errorMessage = {
          id: Date.now() + 1,
          type: 'bot',
          content: 'Desculpe, ocorreu um erro. Tente novamente.',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      }

      setIsTyping(false);
    }, 1500);
  };

  const processMessage = async (message) => {
    const lowerMessage = message.toLowerCase();

    if (lowerMessage.includes('estatística') || lowerMessage.includes('dashboard') || lowerMessage.includes('resumo')) {
      try {
        const statsResponse = await api.get('/dashboard/stats');
        const stats = statsResponse.data;

        return {
          content: `📊 **Resumo do seu CRM:**\n\n• Total de Leads: **${stats.total_leads || 0}**\n• Leads Novos: **${stats.new_leads || 0}**\n• Leads Qualificados: **${stats.qualified_leads || 0}**\n• Vendas Fechadas: **${stats.won_deals || 0}**`,
          quickReplies: ['Ver pipeline', 'Análise detalhada', 'Gamificação']
        };
      } catch (error) {
        return {
          content: 'Não consegui acessar as estatísticas no momento. Tente novamente.',
          quickReplies: ['Tentar novamente', 'Outras opções']
        };
      }
    }

    if (lowerMessage.includes('adicionar lead') || lowerMessage.includes('novo lead') || lowerMessage.includes('criar lead')) {
      return {
        content: 'Para adicionar um novo lead, preciso de algumas informações:\n\n1. Nome completo\n2. Email\n3. Telefone (opcional)\n4. Empresa (opcional)\n\nVocê pode me enviar assim:\n**"Lead: João Silva, joao@empresa.com, 11999999999, Empresa ABC"**',
        quickReplies: ['Exemplo de lead', 'Cancelar'],
        actions: [{ type: 'redirect', target: 'leads' }]
      };
    }

    if (lowerMessage.startsWith('lead:')) {
      return await processLeadCreation(message);
    }

    if (lowerMessage.includes('tarefa') || lowerMessage.includes('task')) {
      return {
        content: '✅ Para criar uma tarefa, me diga:\n**"Tarefa: [título da tarefa]"**\n\nExemplo: "Tarefa: Ligar para cliente João"',
        quickReplies: ['Exemplo tarefa', 'Ver tarefas pendentes']
      };
    }

    if (lowerMessage.startsWith('tarefa:')) {
      const taskTitle = message.substring(7).trim();
      return {
        content: `📋 Tarefa criada: **${taskTitle}**\n\nA tarefa foi adicionada à sua lista. Posso ajudar com mais alguma coisa?`,
        quickReplies: ['Ver tarefas', 'Criar outra tarefa', 'Dashboard']
      };
    }

    if (lowerMessage.includes('ia') || lowerMessage.includes('inteligência') || lowerMessage.includes('análise')) {
      return {
        content: '🤖 **Funcionalidades de IA disponíveis:**\n\n• Análise de Leads com Score\n• Previsão de Vendas\n• Contract Intelligence\n• Assistente de Voz\n• Gamificação Inteligente\n\nQual você gostaria de usar?',
        quickReplies: ['Analisar leads', 'Forecast vendas', 'Contratos', 'Voz'],
        actions: [
          { type: 'redirect', target: 'leads', label: 'Ir para Leads' },
          { type: 'redirect', target: 'contract-intelligence', label: 'Contract Intelligence' }
        ]
      };
    }

    if (lowerMessage.includes('ir para') || lowerMessage.includes('abrir')) {
      if (lowerMessage.includes('lead')) {
        return {
          content: '🎯 Te redirecionando para a página de Leads...',
          actions: [{ type: 'redirect', target: 'leads' }]
        };
      }
      if (lowerMessage.includes('pipeline')) {
        return {
          content: '🎯 Te redirecionando para o Pipeline...',
          actions: [{ type: 'redirect', target: 'pipeline' }]
        };
      }
      if (lowerMessage.includes('gamificação')) {
        return {
          content: '🎯 Te redirecionando para Gamificação...',
          actions: [{ type: 'redirect', target: 'gamification' }]
        };
      }
    }

    if (lowerMessage.includes('ajuda') || lowerMessage.includes('help') || lowerMessage.includes('comandos')) {
      return {
        content: `🆘 **Comandos disponíveis:**\n\n• **"Ver estatísticas"** - Resumo do CRM\n• **"Adicionar lead"** - Criar novo lead\n• **"Criar tarefa"** - Nova tarefa\n• **"Análise de IA"** - Funcionalidades IA\n• **"Ir para [página]"** - Navegar\n\n💡 **Dicas:**\n• Use comandos de voz no Dashboard\n• Analise contratos com IA\n• Acompanhe sua gamificação`,
        quickReplies: ['Ver estatísticas', 'Funcionalidades IA', 'Dashboard']
      };
    }

    if (lowerMessage.includes('oi') || lowerMessage.includes('olá') || lowerMessage.includes('hello')) {
      return {
        content: 'Olá! 😊 Aqui é a Maya. Como posso te ajudar hoje?',
        quickReplies: ['Ver estatísticas', 'Ajuda', 'Funcionalidades IA']
      };
    }

    return {
      content: 'Não entendi completamente. Posso te ajudar com:\n\n• Ver estatísticas do CRM\n• Adicionar novos leads\n• Criar tarefas\n• Análises com IA\n• Navegação entre páginas\n\nO que você gostaria de fazer?',
      quickReplies: ['Ver estatísticas', 'Adicionar lead', 'Análise IA', 'Ajuda']
    };
  };

  const processLeadCreation = async (message) => {
    try {
      const leadData = message.substring(5).split(',').map(item => item.trim());

      if (leadData.length < 2) {
        return {
          content: 'Formato incorreto. Use:\n**"Lead: Nome, email@exemplo.com, telefone, empresa"**',
          quickReplies: ['Tentar novamente', 'Cancelar']
        };
      }

      const newLead = {
        name: leadData[0],
        email: leadData[1],
        phone: leadData[2] || '',
        company: leadData[3] || '',
        source: 'chatbot'
      };

      return {
        content: `✅ **Lead criado com sucesso!**\n\n👤 **${newLead.name}**\n📧 ${newLead.email}\n${newLead.phone ? '📱 ' + newLead.phone + '\n' : ''}${newLead.company ? '🏢 ' + newLead.company : ''}\n\nO lead foi adicionado ao sistema. Posso ajudar com mais alguma coisa?`,
        quickReplies: ['Ver leads', 'Adicionar outro', 'Analisar com IA'],
        actions: [{ type: 'redirect', target: 'leads', label: 'Ver todos os leads' }]
      };
    } catch (error) {
      return {
        content: 'Erro ao criar o lead. Verifique os dados e tente novamente.',
        quickReplies: ['Tentar novamente', 'Cancelar']
      };
    }
  };

  const handleQuickReply = (reply) => {
    handleSendMessage(reply);
  };

  const handleAction = (action) => {
    if (action.type === 'redirect') {
      toast.success(`Redirecionando para ${action.target}...`);
    }
  };

  const formatMessage = (content) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br>');
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <div className="relative w-14 h-14">
          <Button
            onClick={onToggle}
            className="w-full h-full rounded-full ring-4 ring-blue-200 shadow-[0_0_16px_4px_rgba(37,99,235,0.35)] p-0 overflow-hidden"
            aria-label="Falar com a Maya"
          >
            <img src="/MAYA.png" alt="Maya" className="w-full h-full object-cover object-top" />
          </Button>
          <span className="absolute bottom-0.5 right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-white pointer-events-none" title="Maya está online" />
        </div>
      </div>
    );
  }

  const hasMessages = messages.length > 0;

  return (
    <div className="fixed bottom-4 right-4 left-4 sm:left-auto w-auto sm:w-96 h-[70vh] sm:h-[560px] max-h-[560px] z-50">
      <Card className="h-full shadow-2xl border-0 bg-white flex flex-col overflow-hidden">
        {/* Header */}
        <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center space-x-3">
              <span className="w-9 h-9 rounded-full bg-white/20 shrink-0 overflow-hidden">
                <img src="/MAYA.png" alt="Maya" className="w-full h-full object-cover" />
              </span>
              <div className="flex flex-col leading-tight">
                <span>Maya</span>
                <span className="text-xs font-normal text-white/80">Assistente IA do NexoSynX</span>
              </div>
            </CardTitle>
            <div className="flex items-center">
              <Button
                onClick={handleNewConversation}
                variant="ghost"
                size="sm"
                title="Nova conversa"
                aria-label="Iniciar nova conversa"
                className="text-white hover:bg-white/20"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
              </Button>
              <Button
                onClick={handleOpenHistory}
                variant="ghost"
                size="sm"
                title="Histórico de conversas"
                aria-label="Ver histórico de conversas"
                className="text-white hover:bg-white/20"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12a9 9 0 109-9 9.75 9.75 0 00-6.74 2.74L3 8" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3v5h5M12 7v5l4 2" />
                </svg>
              </Button>
              <Button
                onClick={onToggle}
                variant="ghost"
                size="sm"
                title="Fechar"
                aria-label="Fechar"
                className="text-white hover:bg-white/20"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 p-0 flex flex-col min-h-0">
          {showHistory ? (
            /* ── Histórico de conversas ── */
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {conversations.length === 0 ? (
                <div className="text-center text-slate-400 text-sm py-12">
                  Nenhuma conversa anterior ainda.
                </div>
              ) : (
                conversations.map(conv => (
                  <button
                    key={conv.id}
                    onClick={() => handleSelectConversation(conv)}
                    className="w-full text-left bg-gray-50 hover:bg-blue-50 border border-gray-100 rounded-xl p-3 transition-colors flex items-start justify-between gap-2 group"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{conv.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {new Date(conv.updatedAt).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <span
                      onClick={(e) => handleDeleteConversation(e, conv.id)}
                      className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-opacity shrink-0 text-sm"
                      title="Excluir conversa"
                    >
                      🗑️
                    </span>
                  </button>
                ))
              )}
            </div>
          ) : !hasMessages ? (
            /* ── Tela de boas-vindas ── */
            <div className="flex-1 overflow-y-auto p-6 flex flex-col items-center text-center">
              <div className="relative w-20 h-20 shrink-0 mt-2 mx-auto">
                <div className="w-full h-full aspect-square rounded-full overflow-hidden ring-4 ring-blue-200 shadow-[0_0_16px_4px_rgba(37,99,235,0.35)]">
                  <img src="/MAYA.png" alt="Maya" className="w-full h-full object-cover object-top" />
                </div>
                <span className="absolute bottom-0.5 right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-white" title="Maya está online" />
              </div>
              <h3 className="mt-4 text-lg font-bold text-slate-900">Olá! Sou a Maya 👋</h3>
              <p className="text-sm text-slate-500 mt-1 max-w-[280px]">
                Especialista no NexoSynX. Posso te orientar em qualquer função do sistema.
              </p>

              <p className="mt-6 text-xs font-bold tracking-wide text-slate-400">TENTE PERGUNTAR</p>
              <div className="mt-3 w-full space-y-2">
                {SUGGESTED_QUESTIONS.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => handleSendMessage(q)}
                    className="w-full text-left text-sm text-slate-700 bg-white border border-gray-200 rounded-full px-4 py-2.5 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* ── Mensagens ── */
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-lg p-3 ${
                    message.type === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-slate-900'
                  }`}>
                    <div
                      className="text-sm"
                      dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }}
                    />

                    {message.quickReplies && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {message.quickReplies.map((reply, index) => (
                          <Button
                            key={index}
                            onClick={() => handleQuickReply(reply)}
                            variant="outline"
                            size="sm"
                            className="text-xs h-8"
                          >
                            {reply}
                          </Button>
                        ))}
                      </div>
                    )}

                    {message.actions && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {message.actions.map((action, index) => (
                          <Button
                            key={index}
                            onClick={() => handleAction(action)}
                            size="sm"
                            className="text-xs h-8"
                          >
                            {action.label || action.target}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-lg p-3">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Input Area */}
          {!showHistory && (
            <div className="border-t p-4 shrink-0">
              <div className="flex space-x-2">
                <Input
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Pergunte algo à Maya..."
                  className="flex-1 rounded-full"
                />
                <Button
                  onClick={() => handleSendMessage()}
                  disabled={!inputMessage.trim() || isTyping}
                  className="rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shrink-0"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
